import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
    BarChart3, Users, Globe, Smartphone, Monitor, Tablet,
    Eye, Target, ExternalLink, RefreshCw, AlertTriangle, Copy, Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import ConversionFunnel from './ConversionFunnel';

const SOURCE_LABELS = {
    youtube: 'YouTube',
    google_ads: 'Google Ads',
    google_organic: 'Google Orgánico',
    facebook_ads: 'Facebook Ads',
    facebook: 'Facebook',
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    telegram: 'Telegram',
    twitter: 'X (Twitter)',
    linkedin: 'LinkedIn',
    tiktok: 'TikTok',
    directo: 'Directo',
    otro: 'Otro',
};

const SOURCE_COLORS = {
    youtube: 'bg-red-500',
    google_ads: 'bg-amber-500',
    google_organic: 'bg-emerald-500',
    facebook_ads: 'bg-indigo-500',
    facebook: 'bg-blue-500',
    whatsapp: 'bg-green-500',
    instagram: 'bg-pink-500',
    telegram: 'bg-sky-500',
    twitter: 'bg-slate-800',
    linkedin: 'bg-blue-700',
    tiktok: 'bg-violet-600',
    directo: 'bg-slate-500',
    otro: 'bg-slate-400',
};

const DEVICE_ICONS = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };

function Tile({ label, value, hint, accent }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
            <p className={cn('mt-1 font-mono text-2xl font-bold', accent || 'text-slate-900')}>
                {typeof value === 'number' ? value.toLocaleString('es-CL') : value}
            </p>
            {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
        </div>
    );
}

export default function AnalyticsDashboard() {
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [conversion, setConversion] = useState(null);
    const [audit, setAudit] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [o, c, a] = await Promise.all([
                api.get('/admin/analytics/overview'),
                api.get('/admin/analytics/conversion-por-fuente'),
                api.get('/admin/analytics/youtube-audit'),
            ]);
            setOverview(o.data);
            setConversion(c.data);
            setAudit(a.data);
        } catch (e) {
            toast.error('No se pudieron cargar las métricas. ' + (e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const copyUrl = (url) => {
        navigator.clipboard.writeText(url);
        toast.success('URL copiada al portapapeles');
    };

    if (loading || !overview) {
        return (
            <div className="flex h-40 items-center justify-center" data-testid="analytics-loading">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    const fuentesMax = Math.max(...(overview.fuentes || []).map((f) => f.visitas), 1);
    const horasMax = Math.max(...(overview.horas || []).map((h) => h.n), 1);
    const devicesTotal = (overview.devices || []).reduce((s, d) => s + d.n, 0) || 1;

    return (
        <div className="space-y-6" data-testid="analytics-dashboard">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <BarChart3 className="h-6 w-6 text-slate-700" />
                        Analytics y Conversión
                    </h2>
                    <p className="text-sm text-slate-500">Tráfico, fuentes, dispositivos y conversión por origen.</p>
                </div>
                <Button onClick={load} variant="outline" size="sm" className="gap-2" data-testid="analytics-refresh">
                    <RefreshCw className="h-4 w-4" />
                    Recargar
                </Button>
            </div>

            {/* Tiles tráfico + únicos + registros */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Tile label="Visitas 24h" value={overview.trafico.trafico_24h} accent="text-blue-700" />
                <Tile label="Visitas 7d" value={overview.trafico.trafico_7d} accent="text-blue-700" />
                <Tile label="Visitas 30d" value={overview.trafico.trafico_30d} accent="text-blue-700" />
                <Tile label="Total acumulado" value={overview.trafico.trafico_total} accent="text-slate-900" />
                <Tile label="Únicos 7d" value={overview.unicos['7d']} hint="visitantes diferentes" accent="text-emerald-700" />
                <Tile label="Únicos 30d" value={overview.unicos['30d']} accent="text-emerald-700" />
                <Tile label="Registros 30d" value={overview.registros['30d']} hint={`Total: ${overview.registros.total.toLocaleString('es-CL')}`} accent="text-violet-700" />
                <Tile
                    label="Conversión 30d"
                    value={`${overview.conversion_global_30d}%`}
                    hint="únicos → registros"
                    accent={overview.conversion_global_30d >= 3 ? 'text-emerald-700' : 'text-amber-700'}
                />
            </div>

            {/* Auditoría YouTube */}
            {audit && (
                <Card className="border-amber-200 bg-amber-50/40" data-testid="youtube-audit">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 font-heading text-base text-slate-900">
                            <AlertTriangle className="h-4 w-4 text-amber-700" />
                            Auditoría YouTube · {audit.ventana}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            <Tile label="Vía UTM" value={audit.youtube_via_utm} accent="text-emerald-700" />
                            <Tile label="Vía referrer" value={audit.youtube_via_referrer} accent="text-emerald-700" />
                            <Tile label="Total YouTube" value={audit.youtube_total_clasificado} accent="text-slate-900" />
                            <Tile label="Directo" value={audit.directo_total} hint="potencial YouTube App" accent="text-amber-700" />
                            <Tile label="Otro" value={audit.otro_total} accent="text-slate-700" />
                        </div>
                        <p className="text-xs text-amber-900 leading-relaxed bg-amber-100/50 p-3 rounded-lg">
                            <strong>Diagnóstico:</strong> {audit.diagnostico}
                        </p>
                        <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold text-slate-700">URL sugerida para YouTube:</span>
                            <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded text-slate-800 flex-1 truncate">
                                {audit.url_sugerida_youtube}
                            </code>
                            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => copyUrl(audit.url_sugerida_youtube)} data-testid="copy-yt-url">
                                <Copy className="h-3.5 w-3.5" />
                                Copiar
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Fuentes de tráfico */}
            <Card data-testid="sources-block">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 font-heading text-base">
                        <Eye className="h-4 w-4" />
                        Fuentes de tráfico · últimos 30 días
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {overview.fuentes?.length ? (
                        <div className="space-y-2">
                            {overview.fuentes.map((f) => (
                                <div key={f.source} className="flex items-center gap-3">
                                    <span className="w-32 text-sm text-slate-700 shrink-0">{SOURCE_LABELS[f.source] || f.source}</span>
                                    <div className="flex-1 h-7 rounded-md bg-slate-100 overflow-hidden">
                                        <div
                                            className={cn('h-full rounded-md flex items-center justify-end pr-2 text-[10px] text-white font-semibold', SOURCE_COLORS[f.source] || 'bg-slate-400')}
                                            style={{ width: `${Math.max(2, (f.visitas / fuentesMax) * 100)}%` }}
                                        >
                                            {f.visitas}
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="text-[10px] font-mono shrink-0">{f.unicos} únicos</Badge>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-slate-400 text-center py-6">Sin tráfico en este rango.</p>
                    )}
                </CardContent>
            </Card>

            {/* Conversión por fuente */}
            <Card data-testid="conversion-table">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 font-heading text-base">
                        <Target className="h-4 w-4" />
                        Conversión por fuente · últimos 30 días
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {conversion?.tabla?.length ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
                                        <th className="text-left py-2 px-2 font-medium">Fuente</th>
                                        <th className="text-right py-2 px-2 font-medium">Visitas únicas</th>
                                        <th className="text-right py-2 px-2 font-medium">Registros</th>
                                        <th className="text-right py-2 px-2 font-medium">Conversión</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {conversion.tabla.map((row) => (
                                        <tr key={row.source} className="border-b border-slate-100">
                                            <td className="py-2 px-2 text-slate-800">
                                                <span className={cn('inline-block h-2 w-2 rounded-full mr-2 align-middle', SOURCE_COLORS[row.source] || 'bg-slate-400')} />
                                                {SOURCE_LABELS[row.source] || row.source}
                                            </td>
                                            <td className="text-right py-2 px-2 font-mono">{row.visitas}</td>
                                            <td className="text-right py-2 px-2 font-mono">{row.registros}</td>
                                            <td className={cn('text-right py-2 px-2 font-mono font-semibold', row.conversion >= 3 ? 'text-emerald-700' : row.conversion >= 1 ? 'text-amber-700' : 'text-slate-500')}>
                                                {row.conversion}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-400 text-center py-4">Aún no hay registros con atribución de fuente.</p>
                    )}
                </CardContent>
            </Card>

            {/* Dispositivos + Países + Horas */}
            <div className="grid lg:grid-cols-3 gap-4">
                <Card data-testid="devices-block">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 font-heading text-base">
                            <Smartphone className="h-4 w-4" />
                            Dispositivos
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {(overview.devices || []).map((d) => {
                            const Icon = DEVICE_ICONS[d.device] || Monitor;
                            const pct = ((d.n / devicesTotal) * 100).toFixed(1);
                            return (
                                <div key={d.device} className="flex items-center gap-3">
                                    <Icon className="h-4 w-4 text-slate-600" />
                                    <span className="text-sm text-slate-700 capitalize w-20">{d.device}</span>
                                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                        <div className="h-full bg-slate-700 rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-xs text-slate-500 font-mono w-16 text-right">{pct}%</span>
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>

                <Card data-testid="countries-block">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 font-heading text-base">
                            <Globe className="h-4 w-4" />
                            Países (30d)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {overview.paises?.length ? (
                            <ul className="space-y-1.5">
                                {overview.paises.map((p) => (
                                    <li key={p.country} className="flex items-center justify-between text-sm">
                                        <span className="text-slate-700">{p.country}</span>
                                        <Badge variant="outline" className="font-mono text-[10px]">{p.n}</Badge>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs text-slate-400 leading-relaxed">
                                Sin geo-headers detectados. Activa Cloudflare o configura un CDN que envíe <code className="bg-slate-100 px-1 rounded text-[10px]">cf-ipcountry</code>.
                            </p>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="hours-block">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 font-heading text-base">
                            <Users className="h-4 w-4" />
                            Distribución por hora (UTC)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-end gap-[2px] h-24">
                            {Array.from({ length: 24 }).map((_, h) => {
                                const item = overview.horas.find((x) => x.hour === h);
                                const n = item?.n || 0;
                                const pct = (n / horasMax) * 100;
                                return (
                                    <div key={h} className="flex-1 flex flex-col items-center justify-end" title={`${h}:00 → ${n} visitas`}>
                                        <div
                                            className="w-full rounded-t bg-blue-600/80 hover:bg-blue-700 transition-colors"
                                            style={{ height: `${Math.max(2, pct)}%` }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                            <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Conversion Funnel */}
            <ConversionFunnel />

            <p className="text-[11px] text-slate-400 text-center pt-2">
                <ExternalLink className="inline h-3 w-3 mr-1" />
                Datos generados {new Date(overview.generated_at).toLocaleString('es-CL')}
            </p>
        </div>
    );
}
